import {extent, max} from 'd3-array'
import {sort} from 'timsort'

export interface Connection {
    connection_start: number
    start: number
    disabled: boolean
    values: [number, number, number, number][] // x,y, connection_start, timestamp
    maxIndex: number
    extent: [number, number] // yLimits
    locked: boolean
    duration: number
    max_volume: number
    max_volume_timestamp: number
    max_volume_time: number
    color: string
    pre_flow_in: number
    pre_spp: number
    pre_active_vol: number
    post_flow_in: number
    post_spp: number
    post_active_vol: number
}


interface ConnectionEvent {
    connection: number
    connection_start: number
    depth_value: number
    index: number
    initial_volume: number
    max_volume: number
    max_volume_timestamp: number
    max_volume_time: number
    timestamp: number
    values: [number, number][]
    // [x, y, connection_start, timestamp]
    // x,y => to plot the chart
    // connection_start => to select the point on delanoy
    // timestamp => to activate the point-service.
    maxIndex: number
}


interface StartConnectionEvent{
    info: {
        uom: Record<string, string>
        type: string
    }
}

interface Event {
    layer: number
    assetType: string
    events: StartConnectionEvent | ConnectionEvent[]
    type: string
}

interface Command {
    key: string
}

export const xMax = 15 * 60 * 1000 // 15 minutes

export interface ConnectionFingerprintState {
    connections: Map<number, Connection>
    uom: Record<string, string>
    type: string
    hidden: Set<number>,
    widgetId: number,
    labels: Record<string, string>
    assetType: string[] | string
}

export function getStorageKey(widgetId: number,  type: string): string {
    return [widgetId, type, 'curves'].join('/');
}

export function getHiddenCurves(widgetId:number, type: string): Set<number> {
    const json = localStorage.getItem(getStorageKey(widgetId, type))
    try {
        const arr = JSON.parse(json)
        if (Array.isArray(arr)) {

            return new Set<number>(arr)
        }
    } catch (e) {
    }
    
return new Set<number>();
}

export default (
    state: ConnectionFingerprintState = {
        connections: new Map<number, Connection>(), uom: {}, assetType: '', widgetId: 0, hidden: new Set<number>(),
        type: '',
        labels: undefined
    },
    event: Event | Command
): ConnectionFingerprintState => {
    switch (event?.type) {
        case 'start':
            if (event.layer === 0) {
                state.uom = event.events.info.uom
                state.labels = event.events.info.labels
                state.assetType = event.events.info.assetType
                state.widgetId = event.events.info.widgetId
                state.connections = new Map<number, Connection>()
                state.hidden = getHiddenCurves(state.widgetId, state.type)
            }
            return state

        case 'stop':
            console.log('stop:', event)
            return state

        case 'lock':
            if (state.connections.has(event.key)) {
                state.connections.get(event.key).locked = true
            }
            return state

        case 'unlock':
            if (state.connections.has(event.key)) {
                state.connections.get(event.key).locked = false
            }
            return state

        case 'event':            
            event.events.forEach(ev => {
                const {connection, index, timestamp, value, ...data} = ev

                // special command to cancel the connection
                if (connection === -1) {
                    state.connections.delete(ev.connection_start)
                    return
                }

                if (!state.connections.has(data.connection_start)) {
                    state.connections.set(data.connection_start, {
                        ...data,
                        disabled: state.hidden.has(data.connection_start),
                        values: []
                    })
                }

                const conn = state.connections.get(ev.connection_start)
                // add value and update properties
                conn.locked = ev.locked
                conn.values.push([index, value, ev.connection_start, timestamp])
                conn.duration = data.duration
                conn.max_volume = data.max_volume
                conn.max_volume_timestamp = data.max_volume_timestamp
                conn.max_volume_time = data.max_volume_time
                conn.post_active_vol = data.post_active_vol
                conn.post_flow_in = data.post_flow_in
                conn.post_spp = data.post_spp
            })

            let color = max(state.connections.values(), d => d.color) ?? 0

            // pre-calculate maxIndex and extent for every entry.
            for (const [, item] of state.connections.entries()) {
                item.maxIndex = max(item.values, d => d[0])
                // ignore values with volume < 0 or duration > 15 as these are not
                // going to be shown in the chart for now
                item.extent = extent(
                    item.values.filter(d => d[0] <= xMax && d[1] >= 0),
                    d => d[1]
                )
                color++
                item.color ??= color

                sort(item.values, (a, b) => a[0] - b[0])
            }
            return state
            
        default:
            return state
    }
}
